import type { PrivacyServiceManifest } from "./types.js";

export interface ScriptOptions {
  async?: boolean;
  defer?: boolean;
  integrity?: string;
  crossOrigin?: "anonymous" | "use-credentials";
  nonce?: string;
  dataset?: Record<string, string>;
}

function domainAllowed(hostname: string, declared: string[]): boolean {
  return declared.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
}

export function assertDeclaredRequestUrl(
  service: PrivacyServiceManifest,
  source: string,
  baseUrl = globalThis.location?.href ?? "https://local.invalid/",
): URL {
  const url = new URL(source, baseUrl);
  const base = new URL(baseUrl);
  if (!["http:", "https:"].includes(url.protocol)) {
    throw new Error(`Service '${service.id}' tried to use unsupported protocol '${url.protocol}'`);
  }
  if (base.protocol === "https:" && url.protocol !== "https:") {
    throw new Error(`Service '${service.id}' tried to downgrade a secure request`);
  }
  if (url.origin !== base.origin && !domainAllowed(url.hostname, service.requestDomains)) {
    throw new Error(
      `Service '${service.id}' tried to use undeclared request domain '${url.hostname}'`,
    );
  }
  return url;
}

export function loadDeclaredScript(
  service: PrivacyServiceManifest,
  source: string,
  options: ScriptOptions = {},
  documentLike: Document = globalThis.document,
): Promise<HTMLScriptElement> {
  const url = assertDeclaredRequestUrl(service, source, documentLike.baseURI);
  return new Promise((resolve, reject) => {
    const script = documentLike.createElement("script");
    script.src = url.href;
    script.async = options.async ?? true;
    script.defer = options.defer ?? false;
    if (options.integrity) script.integrity = options.integrity;
    if (options.crossOrigin) script.crossOrigin = options.crossOrigin;
    if (options.nonce) script.nonce = options.nonce;
    for (const [name, value] of Object.entries(options.dataset ?? {})) {
      script.dataset[name] = value;
    }
    script.dataset.mikoPrivacyService = service.id;
    script.addEventListener("load", () => resolve(script), { once: true });
    script.addEventListener(
      "error",
      () => reject(new Error(`Could not load declared script for '${service.id}'`)),
      { once: true },
    );
    documentLike.head.append(script);
  });
}
